'use client'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'

import { fetchToolbarLinks, TOOLBAR_PATH_MAP } from '@/src/constants/bottomToolbar'
import { useFeatures } from '@/src/hooks/useFeatures'
import { getActiveToolbarTab } from '@/src/utils/navigation/getActiveToolbarTab'

import { BottomToolbar } from './bottomToolbar'

interface BottomToolbarWrapperProps {
    showVisa?: boolean
    showFlightsHotels?: boolean
    /** Server-resolved via showHolidaysServer() */
    showHolidays?: boolean
    topSlot?: React.ReactNode
}

const BottomToolbarWrapper: React.FC<BottomToolbarWrapperProps> = ({
    showVisa,
    showFlightsHotels,
    showHolidays,
    topSlot
}) => {
    const router = useRouter()
    const pathname = usePathname()
    const t = useTranslations('common')
    const locale = useLocale()
    const { OFFERS, FLIGHTS_HOTELS, EXPERIENCE } = useFeatures()

    // Initialize with active tab based on current URL
    const [currentValue, setCurrentValue] = useState<string>(() => getActiveToolbarTab(pathname))

    // Update active tab when pathname changes
    useEffect(() => {
        const activeTab = getActiveToolbarTab(pathname)
        setCurrentValue(activeTab)
    }, [pathname])

    const handleNavigationChange = useCallback(
        (value: string) => {
            const basePath = TOOLBAR_PATH_MAP[value]
            const noLocaleRoutes = ['visa']
            if (!basePath) return

            const path = noLocaleRoutes.includes(value) ? basePath : `/${locale}${basePath}`

            // Hotels is an internal route now, so it falls through to router.push below.
            // fph has no route in this app and stays a hard navigation to the legacy site.
            const externalRoutes: Record<string, string> = {
                fph: `https://www.flyin.com/${locale}/flights-hotels`
            }

            if (externalRoutes[value]) {
                window.location.href = externalRoutes[value]
            } else {
                router.push(path)
            }
        },
        [router, locale]
    )
    // These tabs are gated on the resolved feature config rather than at each call site, so a
    // page that forgets the check (or a new one) cannot surface a tab the host has no product for.
    const toolbarLinks = useMemo(
        () =>
            fetchToolbarLinks({
                showVisa,
                showFlightsHotels: !!showFlightsHotels && !!FLIGHTS_HOTELS,
                showOffers: !!OFFERS,
                showExperience: !!EXPERIENCE,
                showHolidays
            }),
        [showVisa, showFlightsHotels, FLIGHTS_HOTELS, OFFERS, EXPERIENCE, showHolidays]
    )
    // Translate toolbar links
    const translatedToolbarLinks = useMemo(
        () =>
            toolbarLinks.map(link => ({
                ...link,
                label: t(link.label)
            })),
        [t, toolbarLinks]
    )

    return (
        <BottomToolbar
            currentValue={currentValue}
            toolbarLinks={translatedToolbarLinks}
            onNavigationChange={handleNavigationChange}
            topSlot={topSlot}
        />
    )
}

export { BottomToolbarWrapper }
export default BottomToolbarWrapper
